/*!
 * Basic Jane — Relief Finder
 * Guided shopping + education flow for Shopify (Enterprise theme, OS 2.0).
 *
 * HOW IT WORKS
 *  - Live product data (title, price, image, variant id) comes from the
 *    <script data-rf-products> JSON the Liquid section prints.
 *  - Copy, ingredients and scoring live in the CONFIG below so marketing can
 *    edit them in one place.
 *  - Ingredient definitions are read from the server-rendered glossary
 *    (snippets/relief-finder-learn.liquid) so the SEO copy has a single source.
 */
(function () {
  'use strict';
 
  /* ------------------------------------------------------------------
   * 1. CONFIG — edit copy, links and scoring here
   * ------------------------------------------------------------------ */
 
  var LINKS = {
    topicalVsTransdermal: '/blogs/press/cbd-topical-vs-transdermal-why-carrier-molecules-matter-more-than-milligrams',
    neckBack: '/blogs/press/best-cbd-cream-for-neck-shoulder-back',
    joints: '/blogs/press/what-seniors-should-know-about-joint-pain-staying-active-and-wellness-benefits',
    seniorInjuries: '/blogs/press/top-injuries-for-seniors-cbd-wellness-support',
    sleep: '/blogs/press/hops-and-valerian-the-perfect-combination-for-sleep',
    cbg: '/blogs/press/cbg',
    howToUse: '/pages/how-to-you-use-cbd',
    faq: '/pages/faq',
    coa: '/blogs/basic-training',
    bundle3: '/collections/buy-any-3-products-and-save-15',
    subscribe: '/collections/subscribe-and-save',
    samples: '/collections/sample-pack'
  };
 
  var FORMATS = {
    topical: {
      name: 'Topical',
      article: 'a',
      long: 'topical (cream, gel, spray or roller)',
      onset: 'Minutes',
      duration: 'Up to 4 hrs',
      reach: 'Right where you apply it',
      blurb: 'Fast, targeted support you apply only where you need it. Reapply as needed.'
    },
    transdermal: {
      name: 'Transdermal patch',
      article: 'a',
      long: 'transdermal patch',
      onset: '30–60 min',
      duration: 'Up to 12 hrs',
      reach: 'Targeted or whole-body, depending on placement',
      blurb: 'Steady, hands-free support through the skin. Place it on the spot, or on a venous area for whole-body support.'
    },
    oral: {
      name: 'Oral',
      article: 'an',
      long: 'oral product (mint or chew)',
      onset: '30–90 min',
      duration: 'Up to 6–8 hrs',
      reach: 'Whole body',
      blurb: 'Absorbed through digestion for broader, longer support. Start low and go slow.'
    }
  };
 
  // Keys must match the keys printed by sections/relief-finder.liquid
  var PRODUCTS = {
    cream: {
      phrase: 'a rich, cooling cream',
      name: 'Relieve Cream', format: 'topical', type: 'Cream',
      tagline: 'Rich, cooling cream — our most-loved formula for recovery and evenings.',
      detail: '250 mg CBD per 2 oz',
      ingredients: ['cbd', 'menthol', 'peppermint', 'aloe'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    gel: {
      phrase: 'a light, fast-absorbing gel',
      name: 'Relieve Gel', format: 'topical', type: 'Gel',
      tagline: 'Light, fast-absorbing gel that won’t weigh you down — great for daytime.',
      detail: 'With arnica + MSM',
      ingredients: ['cbd', 'menthol', 'aloe', 'arnica', 'msm'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    spray_relieve: {
      phrase: 'a quick-drying cooling spray',
      name: 'Relieve CBD Spray', format: 'topical', type: 'Spray',
      tagline: 'Point-and-spray cooling that dries almost instantly — easy on hard-to-reach spots.',
      detail: 'No-mess application',
      ingredients: ['cbd', 'menthol'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    spray_relax: {
      phrase: 'a calming cooling spray',
      name: 'Relax CBD Spray', format: 'topical', type: 'Spray',
      tagline: 'Cooling relief plus calming lavender and clary sage for the end of the day.',
      detail: 'Evening aromatherapy',
      ingredients: ['cbd', 'menthol', 'lavender', 'clary_sage'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    spray_awake: {
      phrase: 'an energizing cooling spray',
      name: 'Awake CBD Spray', format: 'topical', type: 'Spray',
      tagline: 'Cooling menthol with an uplifting citrus-ginger blend for active days.',
      detail: 'Energizing aromatherapy',
      ingredients: ['cbd', 'menthol', 'awake_oils'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    roller_relax: {
      phrase: 'a calming roll-on oil',
      name: 'Relax Aromatherapy Oil', format: 'topical', type: 'Roller',
      tagline: 'Pocket-size roller with lavender, ylang-ylang and clary sage for wrists and temples.',
      detail: 'Roll-on, travel friendly',
      ingredients: ['cbd', 'lavender', 'ylang_ylang', 'clary_sage'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    roller_relieve: {
      phrase: 'a cooling roll-on oil',
      name: 'Relieve Aromatherapy Oil', format: 'topical', type: 'Roller',
      tagline: 'Precise roll-on with eucalyptus and mint for knuckles, wrists and small areas.',
      detail: 'Roll-on, travel friendly',
      ingredients: ['cbd', 'eucalyptus', 'peppermint'],
      onset: 'Minutes', duration: 'Up to 4 hrs'
    },
    patch: {
      phrase: 'a 12-hour transdermal patch',
      name: 'CBD Patch', format: 'transdermal', type: 'Patch',
      tagline: 'Peel, stick and forget it — steady support for up to 12 hours.',
      detail: '20 mg CBD per patch · 6 per pouch',
      ingredients: ['cbd', 'aloe', 'coq10', 'black_pepper'],
      onset: '30–60 min', duration: 'Up to 12 hrs'
    },
    mints: {
      phrase: 'oral CBD mints',
      name: 'CBD Mints', format: 'oral', type: 'Mints',
      tagline: 'A simple, consistent 5 mg dose in a peppermint mint — easy to fit into your day.',
      detail: '5 mg CBD per mint',
      ingredients: ['cbd', 'peppermint'],
      onset: '30–90 min', duration: 'Up to 6–8 hrs'
    },
    boost_mints: {
      phrase: 'extra-strength oral mints',
      name: 'Boost Mints (CBDA + CBD)', format: 'oral', type: 'Mints',
      tagline: 'Our extra-strength oral, pairing CBD with raw CBDA.',
      detail: 'CBD + CBDA',
      ingredients: ['cbd', 'cbda'],
      onset: '30–90 min', duration: 'Up to 6–8 hrs'
    },
    night_caps: {
      phrase: 'a natural, non-habit-forming sleep aid',
      name: 'Night Caps', format: 'oral', type: 'Capsules',
      tagline: 'Hops + valerian root — a non-habit-forming way to wind down. (No CBD.)',
      detail: 'Hops + valerian root',
      ingredients: ['hops', 'valerian'],
      onset: '30–60 min', duration: 'Overnight'
    },
    sample_topical: { name: 'Topical Sample Pack', format: 'topical', type: 'Sampler', sample: true, ingredients: [] },
    sample_oral: { name: 'Oral Sample Pack', format: 'oral', type: 'Sampler', sample: true, ingredients: [] }
  };
 
  // Tie-breaker: when two products score the same, the earlier one wins.
  var PRIORITY = ['cream', 'patch', 'spray_relieve', 'gel', 'mints', 'boost_mints', 'spray_relax',
    'roller_relax', 'roller_relieve', 'spray_awake', 'night_caps'];
 
  var INGREDIENT_NAMES = {
    cbd: 'CBD (isolate)', cbda: 'CBDA', menthol: 'Menthol', peppermint: 'Peppermint oil', aloe: 'Aloe',
    arnica: 'Arnica', msm: 'MSM', coq10: 'CoQ10', black_pepper: 'Black pepper extract',
    eucalyptus: 'Eucalyptus', lavender: 'Lavender', clary_sage: 'Clary sage', ylang_ylang: 'Ylang-ylang',
    awake_oils: 'Bergamot, orange & ginger', hops: 'Hops', valerian: 'Valerian root'
  };
 
  /*
   * Questions. `summary` fragments build the result sentence:
   *   "For {area.summary} {pattern.summary}, we'd start with a {format}."
   */
  var QUESTIONS = [
    {
      id: 'area',
      title: 'Where do you feel it most?',
      help: 'Pick the one that bothers you most. You’ll see how to cover other areas in your results.',
      options: [
        { v: 'neck', label: 'Neck & shoulders', icon: 'neck', summary: 'neck and shoulder tension' },
        { v: 'back', label: 'Back', icon: 'back', summary: 'back discomfort' },
        { v: 'joints', label: 'Knees, hips & joints', icon: 'joint', summary: 'joint discomfort' },
        { v: 'hands', label: 'Hands & wrists', icon: 'hand', summary: 'hand and wrist discomfort' },
        { v: 'allover', label: 'Several areas / all over', icon: 'body', summary: 'discomfort in several areas' },
        { v: 'sleep', label: 'Trouble unwinding or sleeping', icon: 'moon', summary: 'winding down at night' }
      ]
    },
    {
      id: 'pattern',
      title: 'How would you describe it?',
      showIf: function (a) { return a.area !== 'sleep'; },
      options: [
        { v: 'activity', label: 'Shows up after activity or workouts', summary: 'that shows up after activity' },
        { v: 'comesgoes', label: 'Comes and goes during the day', summary: 'that comes and goes' },
        { v: 'lingering', label: 'Lingers most of the day', summary: 'that lingers most of the day' },
        { v: 'morning', label: 'I’m stiff when I wake up', summary: 'that’s worst first thing in the morning' }
      ]
    },
    {
      id: 'sleepwhy',
      title: 'What gets in the way of rest?',
      showIf: function (a) { return a.area === 'sleep'; },
      options: [
        { v: 'mind', label: 'A busy, racing mind', summary: 'when a busy mind keeps you up' },
        { v: 'body', label: 'Physical discomfort', summary: 'when discomfort keeps you up' },
        { v: 'both', label: 'A bit of both', summary: 'when both body and mind need to settle' }
      ]
    },
    {
      id: 'time',
      title: 'When do you want support most?',
      showIf: function (a) { return a.area !== 'sleep'; },
      options: [
        { v: 'active', label: 'Before or during activity' },
        { v: 'workday', label: 'At work or out and about' },
        { v: 'evening', label: 'Evenings & bedtime' },
        { v: 'allday', label: 'All day or overnight — without reapplying' }
      ]
    },
    {
      id: 'priority',
      title: 'What matters most to you?',
      options: [
        { v: 'cooling', label: 'An immediate cooling feel' },
        { v: 'light', label: 'Light and non-greasy' },
        { v: 'calm', label: 'Calming aromatherapy' },
        { v: 'handsfree', label: 'Set it and forget it' },
        { v: 'wholebody', label: 'Whole-body support, not just one spot' }
      ]
    },
    {
      id: 'exp',
      title: 'Have you tried CBD before?',
      options: [
        { v: 'new', label: 'I’m new to CBD' },
        { v: 'some', label: 'A few times' },
        { v: 'regular', label: 'I use it regularly' }
      ]
    }
  ];
 
  // Points each answer gives each product.
  var SCORES = {
    area: {
      neck: { spray_relieve: 4, patch: 3, cream: 2, gel: 2, roller_relieve: 1 },
      back: { patch: 4, spray_relieve: 3, cream: 2, gel: 1 },
      joints: { cream: 4, patch: 3, gel: 2, boost_mints: 1 },
      hands: { gel: 3, cream: 3, roller_relieve: 2 },
      allover: { mints: 3, boost_mints: 3, patch: 3, cream: 1 },
      sleep: { night_caps: 4, spray_relax: 2, roller_relax: 2, mints: 2 }
    },
    pattern: {
      activity: { gel: 3, spray_relieve: 3, spray_awake: 2, cream: 1 },
      comesgoes: { cream: 2, spray_relieve: 2, gel: 2, roller_relieve: 1, mints: 1 },
      lingering: { patch: 4, boost_mints: 2, cream: 1 },
      morning: { patch: 3, cream: 2, boost_mints: 1 }
    },
    sleepwhy: {
      mind: { night_caps: 3, roller_relax: 2, mints: 1 },
      body: { patch: 3, cream: 2, spray_relax: 2, night_caps: 1 },
      both: { night_caps: 2, spray_relax: 2, patch: 2, mints: 1 }
    },
    time: {
      active: { gel: 3, spray_relieve: 3, spray_awake: 3 },
      workday: { patch: 3, gel: 2, mints: 2, spray_awake: 1, roller_relieve: 1 },
      evening: { cream: 3, spray_relax: 3, roller_relax: 2, night_caps: 1 },
      allday: { patch: 5, boost_mints: 1 }
    },
    priority: {
      cooling: { cream: 3, spray_relieve: 3, gel: 2 },
      light: { gel: 3, spray_relieve: 3, spray_awake: 2 },
      calm: { spray_relax: 3, roller_relax: 3, night_caps: 1 },
      handsfree: { patch: 5, mints: 2 },
      wholebody: { mints: 3, boost_mints: 3, patch: 3 }
    },
    exp: {
      new: { boost_mints: -1 },
      some: {},
      regular: { boost_mints: 1 }
    }
  };
 
  // "Why it fits you" — shown when the answer contributed to the top pick.
  var REASONS = {
    area: {
      neck: 'Neck and shoulders are easy to reach with a quick spray, and a patch stays put on the upper back.',
      back: 'Backs are hard to reach — sprays and patches get there without a helping hand.',
      joints: 'Knees and hips respond well to a rich cream massaged in, or a patch placed right on the spot.',
      hands: 'Hands need something light that absorbs fast, so you can get on with your day.',
      allover: 'When it’s more than one spot, whole-body formats cover more ground than a single application.',
      sleep: 'Your evening routine matters as much as the product — this fits a wind-down ritual.'
    },
    pattern: {
      activity: 'Activity-related soreness suits fast, on-demand formats you can apply before or after.',
      comesgoes: 'On-and-off discomfort suits something you can reach for only when you need it.',
      lingering: 'For discomfort that lingers, longer-lasting delivery means fewer reapplications.',
      morning: 'Morning stiffness is a good case for overnight support while you sleep.'
    },
    sleepwhy: {
      mind: 'Hops and valerian have long been used to help a busy mind settle at bedtime.',
      body: 'If discomfort is what wakes you, overnight support addresses the cause of the restlessness.',
      both: 'A two-part routine lets you settle body and mind at the same time.'
    },
    time: {
      active: 'It absorbs or dries quickly, so it won’t slow you down mid-activity.',
      workday: 'It’s discreet and low-scent for the office or errands.',
      evening: 'It’s designed to be part of an evening routine.',
      allday: 'One application lasts up to 12 hours — no reapplying.'
    },
    priority: {
      cooling: 'Menthol and peppermint give a cooling sensation within seconds.',
      light: 'It’s lightweight and non-greasy.',
      calm: 'Calming essential oils add an aromatherapy moment to your relief.',
      handsfree: 'Truly hands-free: peel, stick and forget it.',
      wholebody: 'It supports the whole body, not just one spot.'
    },
    exp: {
      new: 'A simple, gentle place to start if you’re new to CBD.',
      some: '',
      regular: 'As a regular CBD user, you may appreciate a stronger option.'
    }
  };
 
  var AREA_TIPS = {
    neck: 'Placement tip: for neck and shoulder tension, many customers put a patch on the back of the shoulder or upper back, where it stays put, and keep a spray handy for quick top-ups.',
    back: 'Placement tip: for the lower back, place the patch directly on the area for concentrated support. Sprays are the easiest way to reach it on your own.',
    joints: 'Tip: massage cream into the joint for a minute or two — the massage itself helps. For a chronic trouble spot like a hip or knee, try a patch before a long day.',
    hands: 'Tip: rollers are precise for knuckles and wrists and fit in a pocket. Wash hands after applying if you’ll touch your eyes.',
    allover: 'Tip: for whole-body support from a patch, place it on a venous area such as the inner wrist or inner arm.',
    sleep: 'Tip: build a routine — take Night Caps about 30 minutes before bed, and use a Relax roller on wrists and temples as you wind down.'
  };
 
  var AREA_ARTICLES = {
    neck: [{ t: 'Best CBD products for neck, shoulder & back', u: LINKS.neckBack }],
    back: [{ t: 'Best CBD products for neck, shoulder & back', u: LINKS.neckBack }],
    joints: [
      { t: 'What seniors should know about joint pain & staying active', u: LINKS.joints },
      { t: 'Top injuries for seniors & CBD wellness support', u: LINKS.seniorInjuries }
    ],
    hands: [{ t: 'How to use CBD topicals', u: LINKS.howToUse }],
    allover: [{ t: 'How to use CBD: topical, oral & transdermal', u: LINKS.howToUse }],
    sleep: [{ t: 'Hops and valerian: the perfect combination for sleep', u: LINKS.sleep }]
  };
 
  var FORMAT_ARTICLES = {
    topical: [{ t: 'Topical vs transdermal: why carrier molecules matter more than milligrams', u: LINKS.topicalVsTransdermal }],
    transdermal: [{ t: 'Topical vs transdermal: why carrier molecules matter more than milligrams', u: LINKS.topicalVsTransdermal }],
    oral: [{ t: 'How to use oral CBD — start low, go slow', u: LINKS.howToUse }]
  };
 
  var PAIR_TEXT = {
    'transdermal>topical': 'Wear the patch for steady, hands-free hours, and reach for the {pair} when you want a quick cooling top-up.',
    'topical>transdermal': 'Use the {primary} for fast, on-the-spot relief, and add a patch on days you need longer-lasting support.',
    'topical>oral': 'Pair targeted relief from the {primary} with whole-body support from {pair}.',
    'oral>topical': 'Let {primary} work from the inside while the {pair} targets specific spots.',
    'transdermal>oral': 'Combine the patch’s 12-hour steady support with {pair} for whole-body balance.',
    'oral>transdermal': 'Add a patch on long days or overnight for steady, hands-free support alongside {primary}.'
  };
 
  /* ------------------------------------------------------------------
   * 2. Helpers
   * ------------------------------------------------------------------ */
 
  var ICONS = {
    neck: '<path d="M12 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"/><path d="M10 9v2l-5 2.5V21M14 9v2l5 2.5V21"/><path d="M8.5 12.5c1 .6 2.2 1 3.5 1s2.5-.4 3.5-1" stroke-dasharray="2 2"/>',
    back: '<path d="M12 3v18"/><path d="M9 6h6M8.5 10h7M8 14h8M8.5 18h7"/>',
    joint: '<path d="M8 3v7a4 4 0 0 0 8 0V3"/><circle cx="12" cy="14" r="2.5"/><path d="M10 17l-1 4M14 17l1 4"/>',
    hand: '<path d="M8 13V6a1.5 1.5 0 0 1 3 0v5M11 11V4.5a1.5 1.5 0 0 1 3 0V11M14 11V6a1.5 1.5 0 0 1 3 0v7c0 4-2.5 8-6 8-2.5 0-4-1.5-5.5-4L4 13.5a1.5 1.5 0 0 1 2.5-1.5L8 14"/>',
    body: '<circle cx="12" cy="4.5" r="2"/><path d="M5 9h14M12 9v6M12 15l-3 6M12 15l3 6"/>',
    moon: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"/>'
  };
 
  function icon(name) {
    if (!ICONS[name]) return '';
    return '<svg class="rf-opt__icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' + ICONS[name] + '</svg>';
  }
 
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
 
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
 
  function url(path) {
    if (!path) return '#';
    if (/^https?:\/\//.test(path)) return path;
    return root().replace(/\/$/, '') + path;
  }
 
  function track(event, data) {
    var payload = Object.assign({ event: event }, data || {});
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) { /* noop */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, data || {});
      }
    } catch (e) { /* noop */ }
  }
 
  function findOption(q, v) {
    for (var i = 0; i < q.options.length; i++) if (q.options[i].v === v) return q.options[i];
    return null;
  }
 
  function getQuestion(id) {
    for (var i = 0; i < QUESTIONS.length; i++) if (QUESTIONS[i].id === id) return QUESTIONS[i];
    return null;
  }
 
  /* ------------------------------------------------------------------
   * 3. The finder
   * ------------------------------------------------------------------ */
 
  function ReliefFinder(el) {
    this.el = el;
    this.app = el.querySelector('[data-rf-app]');
    this.afterAdd = el.getAttribute('data-after-add') || 'cart';
    this.preview = el.hasAttribute('data-rf-preview');
    this.live = {};
    var json = el.querySelector('[data-rf-products]');
    if (json) {
      try { this.live = JSON.parse(json.textContent); } catch (e) { this.live = {}; }
    }
    var missing = Object.keys(PRODUCTS).filter(function (k) { return !this.live[k] || this.live[k].found === false; }, this);
    if (!this.preview && missing.length && window.console) {
      console.warn('[Relief Finder] These products were not found or have no handle set, so they are skipped:', missing.join(', '));
    }
    if (!this.preview && window.Shopify && window.Shopify.designMode && missing.length) {
      var warn = document.createElement('p');
      warn.className = 'rf-admin-note';
      warn.textContent = 'Editor note (shoppers don’t see this): these products weren’t found — check their handles in section settings: ' + missing.join(', ');
      el.querySelector('.rf__inner').insertBefore(warn, this.app);
    }
    this.answers = {};
    this.step = 0;
    this.started = false;
 
    var preset = el.getAttribute('data-preset-area');
    var params = new URLSearchParams(window.location.search);
    var fromUrl = false;
    var self = this;
    QUESTIONS.forEach(function (q) {
      var v = params.get(q.id);
      if (v && findOption(q, v)) { self.answers[q.id] = v; fromUrl = true; }
    });
    if (!this.answers.area && preset && findOption(QUESTIONS[0], preset)) this.answers.area = preset;
    this.presetArea = this.answers.area || null;
 
    this.bind();
    if (fromUrl && this.isComplete()) {
      this.renderResult(true);
    } else {
      this.step = this.firstUnanswered();
      this.renderQuestion();
    }
  }
 
  ReliefFinder.prototype.visible = function () {
    var a = this.answers;
    return QUESTIONS.filter(function (q) { return !q.showIf || q.showIf(a); });
  };
 
  ReliefFinder.prototype.firstUnanswered = function () {
    var vis = this.visible();
    for (var i = 0; i < vis.length; i++) if (!this.answers[vis[i].id]) return i;
    return vis.length;
  };
 
  ReliefFinder.prototype.isComplete = function () {
    return this.firstUnanswered() >= this.visible().length;
  };
 
  ReliefFinder.prototype.productData = function (key) {
    var meta = PRODUCTS[key];
    var live = this.live[key] || {};
    return {
      key: key,
      name: live.title || meta.name,
      url: live.url || null,
      price: live.price || '',
      image: live.image || null,
      variantId: live.variantId || null,
      variantTitle: live.variantTitle || '',
      hasVariants: !!live.hasVariants,
      available: live.available !== false,
      found: live.found !== false,
      meta: meta
    };
  };
 
  ReliefFinder.prototype.bind = function () {
    var self = this;
    this.el.addEventListener('click', function (e) {
      var t = e.target.closest('[data-rf-action]');
      if (!t || !self.el.contains(t)) return;
      var action = t.getAttribute('data-rf-action');
      if (action === 'answer') self.answer(t.getAttribute('data-q'), t.getAttribute('data-v'));
      else if (action === 'back') self.back();
      else if (action === 'restart') self.restart();
      else if (action === 'edit') self.edit();
      else if (action === 'ingredient') self.toggleIngredient(t);
      else if (action === 'share') self.share(t);
      else if (action === 'change-area') { delete self.answers.area; self.presetArea = null; self.step = 0; self.renderQuestion(); }
    });
 
    this.el.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-rf-form]');
      if (form && self.el.contains(form)) self.submit(form, e);
    });
 
    // Open glossary <details> when someone follows an #rf-ing-* link
    function openHash() {
      var id = window.location.hash.slice(1);
      if (!id || id.indexOf('rf-') !== 0) return;
      var target = document.getElementById(id);
      if (target && target.tagName === 'DETAILS') target.open = true;
    }
    window.addEventListener('hashchange', openHash);
    openHash();
  };
 
  ReliefFinder.prototype.answer = function (qid, v) {
    if (!this.started) { this.started = true; track('relief_finder_start', { preset_area: this.presetArea || '' }); }
    this.answers[qid] = v;
    track('relief_finder_answer', { question: qid, answer: v });
 
    // Drop answers that no longer apply (e.g. switching to/from the sleep path)
    var vis = this.visible().map(function (q) { return q.id; });
    var self = this;
    Object.keys(this.answers).forEach(function (k) { if (vis.indexOf(k) === -1) delete self.answers[k]; });
 
    var idx = vis.indexOf(qid);
    this.step = idx + 1;
    if (this.step >= vis.length) this.renderResult();
    else this.renderQuestion();
  };
 
  ReliefFinder.prototype.back = function () {
    if (this.step > 0) { this.step--; this.renderQuestion(); }
  };
 
  ReliefFinder.prototype.restart = function () {
    this.answers = {};
    this.presetArea = null;
    this.editing = false;
    this.step = 0;
    this.clearUrl();
    this.renderQuestion();
    track('relief_finder_restart', {});
  };
 
  ReliefFinder.prototype.edit = function () {
    this.editing = false;
    this.step = 0;
    this.renderQuestion();
  };
 
  ReliefFinder.prototype.focusApp = function () {
    var h = this.app.querySelector('[data-rf-focus]');
    if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
    var rect = this.app.getBoundingClientRect();
    if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
      this.app.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
 
  /* ---------- Question screen ---------- */
 
  ReliefFinder.prototype.renderQuestion = function () {
    var vis = this.visible();
    var q = vis[this.step];
    if (!q) { this.renderResult(); return; }
    var total = vis.length;
    var pct = Math.round((this.step / total) * 100);
    var current = this.answers[q.id];
    var self = this;
 
    var presetNote = '';
    if (this.presetArea && q.id !== 'area' && this.answers.area === this.presetArea) {
      var areaOpt = findOption(QUESTIONS[0], this.presetArea);
      presetNote = '<p class="rf-preset">Finding relief for <strong>' + esc(areaOpt.label.toLowerCase()) +
        '</strong> · <button type="button" class="rf-link" data-rf-action="change-area">Change</button></p>';
    }
 
    var opts = q.options.map(function (o) {
      var pressed = current === o.v;
      return '<button type="button" class="rf-opt' + (o.icon ? ' rf-opt--icon' : '') + (pressed ? ' is-selected' : '') +
        '" data-rf-action="answer" data-q="' + q.id + '" data-v="' + o.v + '" aria-pressed="' + pressed + '">' +
        icon(o.icon) + '<span class="rf-opt__label">' + esc(o.label) + '</span></button>';
    }).join('');
 
    this.app.innerHTML =
      '<div class="rf-card rf-card--question">' +
        '<div class="rf-progress" role="progressbar" aria-valuemin="0" aria-valuemax="' + total + '" aria-valuenow="' + this.step + '" aria-label="Question ' + (this.step + 1) + ' of ' + total + '">' +
          '<span style="width:' + pct + '%"></span></div>' +
        '<p class="rf-step">Step ' + (this.step + 1) + ' of ' + total + '</p>' +
        presetNote +
        '<fieldset class="rf-q"><legend class="rf-q__title" data-rf-focus>' + esc(q.title) + '</legend>' +
        (q.help ? '<p class="rf-q__help">' + esc(q.help) + '</p>' : '') +
        '<div class="rf-opts' + (q.id === 'area' ? ' rf-opts--grid' : '') + '">' + opts + '</div></fieldset>' +
        '<div class="rf-nav">' +
          (this.step > 0 ? '<button type="button" class="rf-link" data-rf-action="back">← Back</button>' : '<span></span>') +
          (Object.keys(this.answers).length ? '<button type="button" class="rf-link" data-rf-action="restart">Start over</button>' : '') +
        '</div>' +
      '</div>';
 
    if (this.started) this.focusApp();
    void self;
  };
 
  /* ---------- Scoring ---------- */
 
  ReliefFinder.prototype.compute = function () {
    var a = this.answers;
    var self = this;
    var totals = {};
    var contrib = {};
    Object.keys(PRODUCTS).forEach(function (k) { totals[k] = 0; contrib[k] = []; });
 
    this.visible().forEach(function (q) {
      var v = a[q.id];
      var map = (SCORES[q.id] || {})[v] || {};
      Object.keys(map).forEach(function (k) {
        totals[k] += map[k];
        if (map[k] > 0) contrib[k].push([q.id, v]);
      });
    });
 
    var eligible = Object.keys(PRODUCTS).filter(function (k) {
      var m = PRODUCTS[k];
      if (m.sample) return false;
      if (k === 'night_caps' && a.area !== 'sleep') return false;
      var d = self.productData(k);
      return d.found && d.available;
    });
 
    eligible.sort(function (x, y) {
      if (totals[y] !== totals[x]) return totals[y] - totals[x];
      var px = PRIORITY.indexOf(x), py = PRIORITY.indexOf(y);
      return (px === -1 ? 99 : px) - (py === -1 ? 99 : py);
    });
 
    var primary = eligible[0];
    var pFormat = PRODUCTS[primary].format;
    var pair = eligible.filter(function (k) { return PRODUCTS[k].format !== pFormat && totals[k] > 0; })[0] || null;
    var alt = eligible.filter(function (k) { return k !== primary && k !== pair && PRODUCTS[k].format === pFormat && totals[k] > 0; })[0] || null;
 
    var reasons = [];
    contrib[primary].forEach(function (pair) {
      var txt = (REASONS[pair[0]] || {})[pair[1]];
      if (txt && reasons.indexOf(txt) === -1) reasons.push(txt);
    });
 
    return { primary: primary, pair: pair, alt: alt, totals: totals, reasons: reasons.slice(0, 3) };
  };
 
  /* ---------- Result screen ---------- */
 
  ReliefFinder.prototype.buyForm = function (key, d, label, cls) {
    // A real Shopify cart form: works with every theme even if JavaScript add-to-cart is blocked.
    return '<form class="rf-buy" method="post" action="' + esc(url('/cart/add')) + '" accept-charset="UTF-8" data-rf-form data-key="' + key + '">' +
      '<input type="hidden" name="form_type" value="product">' +
      '<input type="hidden" name="utf8" value="✓">' +
      '<input type="hidden" name="id" value="' + esc(d.variantId || '') + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<input type="hidden" name="properties[_source]" value="Relief Finder">' +
      '<input type="hidden" name="return_to" value="' + esc(url('/cart')) + '">' +
      '<button type="submit" class="rf-btn ' + cls + '">' + label + '</button></form>';
  };
 
  ReliefFinder.prototype.productCard = function (key, variant) {
    var d = this.productData(key);
    var m = d.meta;
    var self = this;
    var isMain = variant === 'main';
    var img = d.image
      ? '<img src="' + esc(d.image) + '" alt="' + esc(d.name) + '" loading="lazy" width="240" height="240" onerror="this.parentNode.classList.add(\'is-empty\');this.remove()">'
      : '';
    var chips = (m.ingredients || []).map(function (id) {
      return '<button type="button" class="rf-chip" data-rf-action="ingredient" data-ing="' + id + '" data-card="' + key + '" aria-expanded="false">' +
        esc(INGREDIENT_NAMES[id] || id) + '</button>';
    }).join('');
 
    var buy = '';
    if (d.variantId || this.preview) {
      buy = this.buyForm(key, d, 'Add to cart' + (d.price ? ' · ' + esc(d.price) : ''), 'rf-btn--primary');
    }
    var details = d.url ? '<a class="rf-btn rf-btn--ghost" href="' + esc(url(d.url)) + '">' + (d.hasVariants ? 'See all sizes' : 'View details') + '</a>' : '';
    var sizeNote = d.hasVariants && d.variantTitle ? '<p class="rf-pcard__size">Adds ' + esc(d.variantTitle) + '</p>' : '';
 
    void self;
    return '<article class="rf-pcard rf-pcard--' + variant + '">' +
      '<div class="rf-pcard__media' + (img ? '' : ' is-empty') + '">' + img + '</div>' +
      '<div class="rf-pcard__body">' +
        '<p class="rf-badge rf-badge--' + m.format + '">' + esc(FORMATS[m.format].name) + ' · ' + esc(m.type) + '</p>' +
        '<h3 class="rf-pcard__title">' + esc(d.name) + '</h3>' +
        (m.tagline ? '<p class="rf-pcard__tag">' + esc(m.tagline) + '</p>' : '') +
        (isMain ? '<dl class="rf-facts"><div><dt>Feel it</dt><dd>' + esc(m.onset) + '</dd></div><div><dt>Lasts</dt><dd>' + esc(m.duration) + '</dd></div><div><dt>Strength</dt><dd>' + esc(m.detail) + '</dd></div></dl>' : '') +
        (chips ? '<div class="rf-ings"><p class="rf-ings__label">What’s inside <span>— tap to learn</span></p><div class="rf-chips">' + chips + '</div><div class="rf-ing-panel" data-panel="' + key + '" hidden></div></div>' : '') +
        '<div class="rf-pcard__actions">' + buy + details + '</div>' + sizeNote +
        '<p class="rf-added" data-added="' + key + '" hidden></p>' +
      '</div></article>';
  };
 
  ReliefFinder.prototype.renderResult = function (restored) {
    var r = this.compute();
    var a = this.answers;
    var pMeta = PRODUCTS[r.primary];
    var fmt = FORMATS[pMeta.format];
    var areaOpt = findOption(getQuestion('area'), a.area);
    var secondQ = a.area === 'sleep' ? getQuestion('sleepwhy') : getQuestion('pattern');
    var secondOpt = findOption(secondQ, a[secondQ.id]);
    var pName = this.productData(r.primary).name;
 
    var sentence = 'For ' + esc(areaOpt.summary) + (secondOpt ? ' ' + esc(secondOpt.summary) : '') +
      ', we’d start with <strong>' + esc(pMeta.phrase || (fmt.article + ' ' + fmt.long)) + '</strong>.';
 
    var reasons = r.reasons.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');
 
    // Format comparison strip
    var strip = Object.keys(FORMATS).map(function (k) {
      var f = FORMATS[k];
      var on = k === pMeta.format;
      return '<div class="rf-fmt' + (on ? ' is-match' : '') + '">' +
        (on ? '<span class="rf-fmt__flag">Your match</span>' : '') +
        '<h4>' + esc(f.name) + '</h4>' +
        '<dl><div><dt>Feel it</dt><dd>' + esc(f.onset) + '</dd></div><div><dt>Lasts</dt><dd>' + esc(f.duration) + '</dd></div><div><dt>Works</dt><dd>' + esc(f.reach) + '</dd></div></dl>' +
        '<p>' + esc(f.blurb) + '</p></div>';
    }).join('');
 
    // Pair
    var pairHtml = '';
    if (r.pair) {
      var pairName = this.productData(r.pair).name;
      var key = pMeta.format + '>' + PRODUCTS[r.pair].format;
      var txt = (PAIR_TEXT[key] || 'Pair it with the {pair}.').replace('{primary}', pName).replace('{pair}', pairName);
      pairHtml = '<section class="rf-block"><h3 class="rf-block__title">Complete your routine</h3>' +
        '<p class="rf-block__lead">' + esc(txt) + '</p>' + this.productCard(r.pair, 'pair') +
        '<p class="rf-save">Mix &amp; match: <a href="' + esc(url(LINKS.bundle3)) + '">buy any 3 products, save 15%</a> · <a href="' + esc(url(LINKS.subscribe)) + '">subscribe &amp; save 15%</a></p></section>';
    }
 
    var altHtml = '';
    if (r.alt) {
      var ad = this.productData(r.alt);
      altHtml = '<p class="rf-alt">Prefer a different feel? Try <a href="' + esc(url(ad.url || '#')) + '">' + esc(ad.name) + '</a> — ' + esc(PRODUCTS[r.alt].tagline || '') + '</p>';
    }
 
    // New to CBD → sampler
    var sampleHtml = '';
    if (a.exp === 'new') {
      var sKey = pMeta.format === 'oral' ? 'sample_oral' : 'sample_topical';
      var sd = this.productData(sKey);
      if (sd.found) {
        sampleHtml = '<aside class="rf-sample"><div><h3>New to CBD? Try before you commit.</h3>' +
          '<p>Our ' + esc(sd.name) + (sd.price ? ' (' + esc(sd.price) + ')' : '') + ' lets you test a few formats and find your favorite.</p></div>' +
          '<div class="rf-sample__actions">' +
          (sd.variantId || this.preview ? this.buyForm(sKey, sd, 'Add sampler', 'rf-btn--secondary') : '') +
          '<a class="rf-link" href="' + esc(url(LINKS.samples)) + '">See all samplers</a></div>' +
          '<p class="rf-added" data-added="' + sKey + '" hidden></p></aside>';
      }
    }
 
    // Learn more
    var seen = {};
    var articles = [].concat(AREA_ARTICLES[a.area] || [], FORMAT_ARTICLES[pMeta.format] || [], [{ t: 'Lab results (COAs) for every batch', u: LINKS.coa }])
      .filter(function (x) { if (seen[x.u]) return false; seen[x.u] = 1; return true; });
    var learn = '<section class="rf-block rf-learnmore"><h3 class="rf-block__title">Want to learn more?</h3><ul>' +
      articles.map(function (x) { return '<li><a href="' + esc(url(x.u)) + '">' + esc(x.t) + ' →</a></li>'; }).join('') +
      (document.getElementById('rf-learn') ? '<li><a href="#rf-learn">Compare every format &amp; ingredient on this page ↓</a></li>' : '') +
      '</ul></section>';
 
    this.app.innerHTML =
      '<div class="rf-card rf-card--result">' +
        '<p class="rf-step">Your relief match</p>' +
        '<h2 class="rf-result__title" data-rf-focus>' + sentence + '</h2>' +
        this.productCard(r.primary, 'main') +
        (reasons ? '<section class="rf-block"><h3 class="rf-block__title">Why it fits you</h3><ul class="rf-reasons">' + reasons + '</ul></section>' : '') +
        (AREA_TIPS[a.area] ? '<p class="rf-tip">' + esc(AREA_TIPS[a.area]) + '</p>' : '') +
        altHtml +
        sampleHtml +
        pairHtml +
        '<section class="rf-block"><h3 class="rf-block__title">How the formats compare</h3><div class="rf-fmts">' + strip + '</div></section>' +
        learn +
        '<div class="rf-nav rf-nav--result">' +
          '<button type="button" class="rf-link" data-rf-action="edit">← Change my answers</button>' +
          '<button type="button" class="rf-link" data-rf-action="share">Copy link to my results</button>' +
          '<button type="button" class="rf-link" data-rf-action="restart">Start over</button>' +
        '</div>' +
      '</div>';
 
    this.writeUrl();
    track('relief_finder_complete', {
      answers: Object.assign({}, a),
      primary: r.primary, primary_format: pMeta.format,
      pair: r.pair || '', restored: !!restored
    });
    this.result = r;
    if (!restored) this.focusApp();
  };
 
  /* ---------- Ingredients ---------- */
 
  ReliefFinder.prototype.toggleIngredient = function (btn) {
    var id = btn.getAttribute('data-ing');
    var card = btn.getAttribute('data-card');
    var panel = this.app.querySelector('[data-panel="' + card + '"]');
    if (!panel) return;
    var wasOpen = btn.getAttribute('aria-expanded') === 'true';
    this.app.querySelectorAll('.rf-chip[data-card="' + card + '"]').forEach(function (c) {
      c.setAttribute('aria-expanded', 'false'); c.classList.remove('is-active');
    });
    if (wasOpen) { panel.hidden = true; return; }
 
    var src = document.getElementById('rf-ing-' + id);
    var body = src ? src.querySelector('.rf-gloss__body') : null;
    panel.innerHTML = '<h4>' + esc(INGREDIENT_NAMES[id] || id) + '</h4>' +
      (body ? body.innerHTML : '<p>More details coming soon.</p>') +
      (src ? '<p><a href="#rf-ing-' + id + '">See the full ingredient guide ↓</a></p>' : '');
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    btn.classList.add('is-active');
    track('relief_finder_ingredient', { ingredient: id, product: card });
  };
 
  /* ---------- Cart ---------- */
 
  ReliefFinder.prototype.submit = function (form, e) {
    var key = form.getAttribute('data-key');
    var variant = form.querySelector('input[name="id"]').value;
    var btn = form.querySelector('button');
    var note = this.app.querySelector('[data-added="' + key + '"]');
    var label = btn.innerHTML;
    var self = this;
    var d = this.productData(key);
    var info = { product: key, variant_id: variant, price: d.price, primary: this.result && this.result.primary };
 
    // Preview file: nothing to add to
    if (this.preview || !variant) {
      e.preventDefault();
      btn.innerHTML = 'Added ✓';
      if (note) { note.hidden = false; note.textContent = 'Preview mode — on your store this adds the product and opens the cart.'; }
      return;
    }
 
    track('relief_finder_add_to_cart', info);
 
    // "Go to cart": let the browser submit the form. Shopify adds the item and
    // redirects to the cart itself — the most reliable path on any theme.
    if (this.afterAdd !== 'stay') {
      btn.innerHTML = 'Adding…';
      return;
    }
 
    // "Stay on page": add in the background; if that fails, fall back to the form.
    e.preventDefault();
    btn.disabled = true;
    btn.innerHTML = 'Adding…';
    fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1, properties: { _source: 'Relief Finder' } }] })
    }).then(function (res) {
      return res.json().then(function (j) {
        if (!res.ok) throw new Error(j.description || j.message || 'Could not add to cart');
        return j;
      });
    }).then(function () {
      btn.disabled = false;
      btn.innerHTML = 'Added ✓';
      btn.classList.add('is-added');
      if (note) { note.hidden = false; note.innerHTML = 'Added to your cart. <a href="' + esc(url('/cart')) + '">View cart →</a>'; }
      document.dispatchEvent(new CustomEvent('relief-finder:added', { detail: { key: key, variantId: variant } }));
      ['cart:refresh', 'cart:updated', 'theme:cart:refresh'].forEach(function (n) {
        document.dispatchEvent(new CustomEvent(n, { bubbles: true }));
      });
      self.refreshCartCount();
    }).catch(function (err) {
      btn.disabled = false;
      btn.innerHTML = label;
      if (window.console) console.warn('[Relief Finder] background add failed, submitting form instead:', err);
      if (err && /sold out|not available|already sold/i.test(err.message)) {
        if (note) { note.hidden = false; note.textContent = err.message; }
        return;
      }
      HTMLFormElement.prototype.submit.call(form);
    });
  };
 
  ReliefFinder.prototype.refreshCartCount = function () {
    fetch(url('/cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count, [data-header-cart-count]').forEach(function (n) {
          n.textContent = cart.item_count;
          n.hidden = false;
        });
      }).catch(function () {});
  };
 
  /* ---------- URL state ---------- */
 
  ReliefFinder.prototype.writeUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    QUESTIONS.forEach(function (q) { params.delete(q.id); });
    var a = this.answers;
    this.visible().forEach(function (q) { if (a[q.id]) params.set(q.id, a[q.id]); });
    var qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
  };
 
  ReliefFinder.prototype.clearUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    QUESTIONS.forEach(function (q) { params.delete(q.id); });
    var qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  };
 
  ReliefFinder.prototype.share = function (btn) {
    var link = window.location.href.split('#')[0];
    var orig = btn.textContent;
    function ok() { btn.textContent = 'Link copied ✓'; setTimeout(function () { btn.textContent = orig; }, 2000); }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(ok, function () { window.prompt('Copy this link:', link); });
    } else {
      window.prompt('Copy this link:', link);
    }
    track('relief_finder_share', {});
  };
 
  /* ------------------------------------------------------------------
   * 4. Boot (also re-inits inside the theme editor)
   * ------------------------------------------------------------------ */
 
  function init(scope) {
    (scope || document).querySelectorAll('[data-relief-finder]').forEach(function (el) {
      if (el.__rf) return;
      el.__rf = new ReliefFinder(el);
    });
  }
 
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
 
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
 
  window.BasicJaneReliefFinder = { init: init, config: { PRODUCTS: PRODUCTS, QUESTIONS: QUESTIONS, SCORES: SCORES, LINKS: LINKS } };
})();